<!--
@component
The docs site's search palette: the dashboard command palette's shell with a
single corpus, the docs search API. Results navigate in-site. Scope tabs
mirror the docs navbar (All, Guide, Examples, Reference); ⇄ switch them, and
each searches only its own section. Recents and Suggested share the dashboard
palette's localStorage stores, so history follows the reader between
modal.com/docs and the dashboard.
-->
<script lang="ts">
  import { goto } from "$app/navigation";

  import { Command, Dialog } from "bits-ui";

  import CmdKTabs from "$lib/command-palette/CmdKTabs.svelte";
  import Footer from "$lib/command-palette/Footer.svelte";
  import Input from "$lib/command-palette/Input.svelte";
  import List from "$lib/command-palette/List.svelte";
  import {
    DOCS_SITE_TABS,
    docsHref,
    docsResultToSiteItem,
    type DocsSearchResult,
    docsSitePath,
    type DocsSiteTab,
    groupDocsResults,
    isInDocsTab,
    loadDocsRecents,
    rememberDocsRecent,
    searchDocsTab,
  } from "$lib/command-palette/utils/docs-search";
  import {
    type FrequencyMap,
    getSuggestedDocs,
    loadFrequency,
    recordSelection,
  } from "$lib/command-palette/utils/frequency";
  import type {
    CommandGroup,
    CommandItem,
  } from "$lib/command-palette/utils/groups";
  import { scrollSelectedItemIntoView } from "$lib/command-palette/utils/scroll-selected-item";

  type Props = {
    open?: boolean;
    placeholder?: string;
    /** Called after a result is opened, e.g. to close a mobile menu. */
    onNavigate?: () => void;
  };

  let {
    open = $bindable(false),
    placeholder = "Search documentation",
    onNavigate,
  }: Props = $props();

  let query = $state("");
  let activeTab = $state<DocsSiteTab>("all");
  // The last completed fetch. While a new query or tab is in flight the
  // previous results keep rendering (dimmed and unselectable) so the list
  // doesn't collapse and re-expand on every keystroke.
  let results = $state<{
    query: string;
    tab: DocsSiteTab;
    results: DocsSearchResult[];
  }>({ query: "", tab: "all", results: [] });
  let pending = $state(false);
  let recents = $state<DocsSearchResult[]>([]);
  let frequency = $state<FrequencyMap>({});
  let navigatingHref = $state<string | null>(null);

  let stale = $derived(
    results.query !== query.trim() || results.tab !== activeTab,
  );
  let activeTabLabel = $derived(
    DOCS_SITE_TABS.find((tab) => tab.id === activeTab)?.label ?? "All",
  );
  let tabPlaceholder = $derived(
    activeTab === "all"
      ? placeholder
      : `Search ${activeTabLabel.toLowerCase()}`,
  );

  // Reset on every close, however it happened (Escape, outside click, a
  // navigation, Cmd+K): bits-ui's onOpenChange doesn't fire for a bound
  // `open = false`, so a query typed before closing would otherwise survive
  // into the next open.
  $effect(() => {
    if (open) {
      recents = loadDocsRecents();
      frequency = loadFrequency();
    } else {
      clearQuery();
      activeTab = "all";
    }
  });

  $effect(() => {
    const trimmed = query.trim();
    const tab = activeTab;
    if (!open || trimmed.length < 2) {
      results = { query: "", tab, results: [] };
      pending = false;
      return;
    }
    pending = true;
    const controller = new AbortController();
    const debounce = setTimeout(() => {
      searchDocsTab(tab, trimmed, { limit: 50, signal: controller.signal })
        .then((hits) => {
          results = { query: trimmed, tab, results: hits };
          pending = false;
        })
        .catch((error: unknown) => {
          // Abort during a stale effect is expected; anything else just
          // leaves the list empty rather than blocking it.
          if (!controller.signal.aborted) {
            results = { query: "", tab, results: [] };
            pending = false;
            console.warn("Docs search failed", error);
          }
        });
    }, 250);
    return () => {
      clearTimeout(debounce);
      controller.abort();
    };
  });

  // At rest each tab shows only its own section's history and entry points.
  let tabRecents = $derived(
    recents.filter((result) => isInDocsTab(result.slug, activeTab)),
  );
  let suggested = $derived(
    getSuggestedDocs(frequency, new Set(recents.map((r) => r.slug)), 50)
      .filter((result) => isInDocsTab(result.slug, activeTab))
      .slice(0, 5),
  );

  function dim(group: CommandGroup): CommandGroup {
    return {
      ...group,
      items: group.items.map((item) => ({ ...item, disabled: true })),
    };
  }

  let groups = $derived.by((): CommandGroup[] => {
    if (query.trim().length < 2) {
      return [
        ...(tabRecents.length
          ? [
              {
                heading: "Recents",
                items: tabRecents.map(docsResultToSiteItem),
              },
            ]
          : []),
        ...(suggested.length
          ? [
              {
                heading: "Suggested",
                items: suggested.map(docsResultToSiteItem),
              },
            ]
          : []),
      ];
    }
    const sections = groupDocsResults(
      results.results,
      20,
      undefined,
      "",
      docsResultToSiteItem,
    ).map((group) => ({
      ...group,
      // On the Reference tab the headings ("Python SDK Reference", "CLI
      // Reference") would repeat the tab name; keep only the surface.
      heading:
        activeTab === "reference"
          ? (group.heading?.replace(/\s+Reference$/, "") ?? null)
          : group.heading,
    }));
    return stale ? sections.map(dim) : sections;
  });

  function clearQuery() {
    query = "";
    results = { query: "", tab: activeTab, results: [] };
  }

  function handleContentKeydown(event: KeyboardEvent) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const index = DOCS_SITE_TABS.findIndex((tab) => tab.id === activeTab);
    const step = event.key === "ArrowRight" ? 1 : -1;
    activeTab =
      DOCS_SITE_TABS[
        (index + step + DOCS_SITE_TABS.length) % DOCS_SITE_TABS.length
      ].id;
  }

  async function navigate(item: CommandItem) {
    const result = [...results.results, ...recents, ...suggested].find(
      (r) => docsSitePath(r.slug) === item.href,
    );
    if (result) {
      rememberDocsRecent(result);
      // Keyed by the canonical docs URL, as the dashboard palette keys its
      // docs picks, so both palettes count the same page together.
      recordSelection(docsHref(result.slug), result);
    }
    navigatingHref = item.href;
    try {
      await goto(item.href);
    } finally {
      navigatingHref = null;
      open = false;
      onNavigate?.();
    }
  }
</script>

<Dialog.Root bind:open>
  <Dialog.Portal>
    <Dialog.Overlay class="fixed inset-0 z-40 bg-scrim" />
    <Dialog.Content
      class="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]"
      onEscapeKeydown={() => (open = false)}
      onkeydown={handleContentKeydown}
      onclick={(e) => {
        // Hide the palette when clicking outside of it
        if (e.target === e.currentTarget) open = false;
      }}
    >
      <div
        class="flex max-h-[75vh] w-full max-w-[672px] flex-col overflow-hidden rounded-lg bg-dialog shadow-2xl"
      >
        <Command.Root
          class="flex min-h-0 flex-1 flex-col"
          shouldFilter={false}
          loop={true}
          onValueChange={scrollSelectedItemIntoView}
        >
          <div class="flex flex-col gap-4 border-b border-secondary px-6 py-4">
            <Input
              bind:value={query}
              onClear={clearQuery}
              placeholder={tabPlaceholder}
            />
            <CmdKTabs
              tabs={DOCS_SITE_TABS}
              selectedTab={activeTab}
              onSelectTab={(tab) => (activeTab = tab as DocsSiteTab)}
            />
          </div>
          <List
            {groups}
            {query}
            emptyTitle={activeTab === "all"
              ? undefined
              : `No results in ${activeTabLabel}`}
            emptyAction={activeTab !== "all" && query.trim()
              ? { label: "Search all", onclick: () => (activeTab = "all") }
              : undefined}
            onSelect={navigate}
            {navigatingHref}
            loadingHint={pending ? "Searching docs…" : null}
          />
        </Command.Root>
        <Footer showChangeTabs />
      </div>
    </Dialog.Content>
  </Dialog.Portal>
</Dialog.Root>
